import React, { useEffect, useMemo } from 'react';
import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Description, FiberNew } from '@material-ui/icons';
import { unpackToDateTime } from '@campfire/hot-date';
import { useCampfireTheme } from '../../../../../../theme/useCampfireTheme';
import { useEndpointFetch } from '../../../../../../global/network/useEndpointFetch';
import { useDownloadCertificate } from '../../../../../../common/certificates/useDownloadCertificate';
import { AchievementBadge } from '../../my-achievements/AchievementBadge';
import { CertificateDownloadButton } from '../../../../../../common/certificates/CertificateDownloadButton';
import { MyAchievementLevel, MyAward } from '../../my-achievements/useMyAchievements';
import { useVolDashboardMyElementsContext } from '../../VolDashboardMyElementsContext';

const useStyles = makeStyles(() =>
  createStyles({
    heading: { fontSize: '20px', fontWeight: 800, color: '#000', marginBottom: '12px' },
    group: { marginBottom: '32px' },
    levels: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '20px',
      padding: '16px 20px',
      background: '#FAFAFA',
      border: '1px solid #EFEFEF',
      borderRadius: '6px',
    },
    level: { width: '84px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' },
    levelName: { fontSize: '13px', fontWeight: 600, color: '#333', textAlign: 'center', lineHeight: 1.25 },
    levelDate: { fontSize: '12px', color: '#767676', textAlign: 'center', lineHeight: 1.25 },
    certificate: {
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      padding: '12px 16px',
      background: '#FAFAFA',
      border: '1px solid #EFEFEF',
      borderRadius: '6px',
      '& + &': { marginTop: '10px' },
    },
    certificateIcon: {
      width: '36px',
      height: '36px',
      borderRadius: '6px',
      background: '#F1F0FE',
      color: '#5A50E0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    certificateTitle: { fontSize: '15px', fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center' },
    certificateMeta: { fontSize: '12px', color: '#767676' },
    empty: { fontSize: '14px', color: '#494949', padding: '2rem 0' },
    progressTrack: { width: '64px', height: '4px', borderRadius: '2px', background: '#E4E4E4', overflow: 'hidden' },
    progressFill: { height: '4px', borderRadius: '2px', background: '#7570EA' },
  })
);

const formatDate = (value: string) => unpackToDateTime(value).toFormat('d MMM yyyy');

const formatValue = (value: number) => Number(value.toFixed(1)).toLocaleString();

interface Group {
  achievementId: string;
  name: string;
  showProgress?: boolean;
  levels: MyAchievementLevel[];
}

export const MyAchievementsTabContent = () => {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const {
    myAchievements: { awards, certificates, achievements, progress, newCount, refetch, loading, loaded },
    newAchievementIds: newIds,
    setNewAchievementIds: setNewIds,
  } = useVolDashboardMyElementsContext();
  const { download, downloading } = useDownloadCertificate();
  const markSeenFetch = useEndpointFetch<{}>('/vm/achievement/mark-seen');

  useEffect(() => {
    if (!loaded || newIds) return;
    setNewIds(
      new Set([
        ...awards.filter((a) => !a.seenAt).map((a) => a.awardedAchievementId),
        ...certificates.filter((c) => !c.seenAt).map((c) => c.volunteerCertificateId),
      ])
    );
    if (newCount > 0) {
      markSeenFetch.run({}).then(() => {
        if (refetch) refetch();
      });
    }
  }, [loaded]);

  const awardsByLevel = useMemo(() => new Map<string, MyAward>(awards.map((a) => [a.level.achievementLevelId, a])), [
    awards,
  ]);

  const groups = useMemo(() => {
    const listed: Group[] = achievements
      .filter((achievement) => achievement.isEnabled)
      .map((achievement) => ({
        achievementId: achievement.achievementId,
        name: achievement.name,
        showProgress: achievement.showProgress,
        levels: [...achievement.levels].sort((a, b) => a.position - b.position),
      }));
    const listedIds = new Set(listed.map((group) => group.achievementId));
    const unlisted = new Map<string, Group>();
    awards
      .filter((award) => !listedIds.has(award.level.achievement.achievementId))
      .forEach((award) => {
        const { achievementId, name } = award.level.achievement;
        const group = unlisted.get(achievementId) ?? { achievementId, name, levels: [] };
        group.levels.push(award.level);
        unlisted.set(achievementId, group);
      });
    return [
      ...listed,
      ...Array.from(unlisted.values()).map((group) => ({
        ...group,
        levels: group.levels.sort((a, b) => a.position - b.position),
      })),
    ];
  }, [achievements, awards]);

  const isNew = (id: string) => Boolean(newIds?.has(id));

  const renderProgress = (group: Group, level: MyAchievementLevel) => {
    const value = progress.get(group.achievementId);
    if (!group.showProgress || value === undefined || !level.threshold) return null;
    const next = group.levels.find((l) => l.threshold && !awardsByLevel.has(l.achievementLevelId));
    if (next?.achievementLevelId !== level.achievementLevelId) return null;
    return (
      <>
        <Box className={classes.progressTrack} title={`${formatValue(value)} of ${formatValue(level.threshold)}`}>
          <Box
            className={classes.progressFill}
            style={{ width: `${Math.min(100, (value / level.threshold) * 100)}%` }}
          />
        </Box>
        <Typography className={classes.levelDate}>
          {`${formatValue(Math.min(value, level.threshold))} / ${formatValue(level.threshold)}`}
        </Typography>
      </>
    );
  };

  return (
    <Box padding='24px 38px'>
      <Typography style={{ color: theme.color.grey.neutral200, marginBottom: '30px' }}>
        Achievements and certificates you have been awarded.
      </Typography>

      {!loading && groups.length === 0 && certificates.length === 0 && (
        <Typography className={classes.empty}>You haven&apos;t been awarded any achievements yet.</Typography>
      )}

      {groups.map((group) => (
        <Box key={group.achievementId} className={classes.group}>
          <Typography className={classes.heading}>{group.name}</Typography>
          <Box className={classes.levels}>
            {group.levels.map((level) => {
              const award = awardsByLevel.get(level.achievementLevelId);
              return (
                <Box key={level.achievementLevelId} className={classes.level}>
                  <AchievementBadge
                    badgeDesign={level.badgeDesign}
                    label={level.name}
                    size={56}
                    held={Boolean(award)}
                    isNew={award ? isNew(award.awardedAchievementId) : false}
                  />
                  <Typography className={classes.levelName}>{level.name}</Typography>
                  {award ? (
                    <Typography className={classes.levelDate}>{formatDate(award.awardedAt)}</Typography>
                  ) : (
                    renderProgress(group, level)
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}

      {certificates.length > 0 && (
        <Box className={classes.group}>
          <Typography className={classes.heading}>Certificates</Typography>
          {certificates.map((certificate) => (
            <Box key={certificate.volunteerCertificateId} className={classes.certificate}>
              <Box className={classes.certificateIcon}>
                <Description fontSize='small' />
              </Box>
              <Box flex={1}>
                <Typography className={classes.certificateTitle}>
                  {certificate.awardedFor || certificate.templateName}
                  {isNew(certificate.volunteerCertificateId) && (
                    <FiberNew titleAccess='New' style={{ color: '#FF7705', marginLeft: 8 }} />
                  )}
                </Typography>
                <Typography className={classes.certificateMeta}>{`Issued ${formatDate(
                  certificate.issuedAt
                )}`}</Typography>
              </Box>
              <CertificateDownloadButton
                disabled={downloading}
                onClick={() => download(certificate.volunteerCertificateId)}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
